import { TOOL_HANDLERS } from "./tools";

const VALID_NAMES = new Set(Object.keys(TOOL_HANDLERS));

function argsToString(args: unknown): string {
  if (typeof args === "string") return args.trim() || "{}";
  if (args && typeof args === "object") return JSON.stringify(args);
  return "{}";
}

function normalizeOne(
  o: unknown,
  index: number
): { id: string; name: string; arguments: string } | null {
  if (!o || typeof o !== "object") return null;
  const rec = o as Record<string, unknown>;

  let name: string | undefined;
  let argsRaw: unknown;
  const fallbackId = `call_text_${index}`;

  if (rec.function && typeof rec.function === "object") {
    const fn = rec.function as Record<string, unknown>;
    name = typeof fn.name === "string" ? fn.name : undefined;
    argsRaw = fn.arguments;
  } else {
    name = typeof rec.name === "string" ? rec.name : undefined;
    argsRaw = rec.arguments;
  }

  const id = typeof rec.id === "string" ? rec.id : fallbackId;

  if (!name || !VALID_NAMES.has(name)) return null;
  return { id, name, arguments: argsToString(argsRaw) };
}

/**
 * Một số LLM (đặc biệt qua Cerebras) ghi tool call vào nội dung assistant dạng JSON
 * thay vì trả về delta.tool_calls. Parse để chạy tool giống luồng native.
 */
export function tryParseToolCallsFromAssistantText(
  text: string
): { id: string; name: string; arguments: string }[] | null {
  const raw = text.trim();
  if (!raw || !raw.includes("{")) return null;

  let jsonStr = raw
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();

  const tryParse = (s: string): unknown => {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  let parsed: unknown = tryParse(jsonStr);
  if (parsed === null) {
    const start = jsonStr.indexOf("{");
    const end = jsonStr.lastIndexOf("}");
    if (start >= 0 && end > start) {
      parsed = tryParse(jsonStr.slice(start, end + 1));
    }
  }
  if (parsed === null) return null;

  const out: { id: string; name: string; arguments: string }[] = [];

  if (Array.isArray(parsed)) {
    for (let i = 0; i < parsed.length; i++) {
      const one = normalizeOne(parsed[i], i);
      if (one) out.push(one);
    }
  } else {
    const one = normalizeOne(parsed, 0);
    if (one) out.push(one);
  }

  return out.length ? out : null;
}

/**
 * Loại JSON mô tả function/tool call khỏi nội dung hiển thị (không ảnh hưởng luồng xử lý phía client).
 */
export function stripToolCallJsonDisplay(text: string): string {
  if (!text?.trim()) return text;

  if (tryParseToolCallsFromAssistantText(text.trim())) {
    return "";
  }

  let s = text.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (block, inner) => {
    const t = String(inner).trim();
    return tryParseToolCallsFromAssistantText(t) ? "" : block;
  });

  const lines = s.split("\n");
  const kept: string[] = [];
  for (const line of lines) {
    const t = line.trim();
    if (t && tryParseToolCallsFromAssistantText(t)) {
      continue;
    }
    kept.push(line);
  }
  s = kept.join("\n");

  return s.replace(/\n{3,}/g, "\n\n").trim();
}
